const getSampleValue = (field) => {
  const type = (field.type || '').toLowerCase();
  if (field.options && field.options.length > 0) {
    return field.options[0].value || field.options[0];
  }
  switch (type) {
    case 'number':
    case 'numeric':
      return 42;
    case 'decimal':
    case 'currency':
      return 19.99;
    case 'date':
      return '2024-01-15';
    case 'datetime':
    case 'date-time':
      return '2024-01-15T10:30:00Z';
    case 'time':
      return '10:30';
    case 'boolean':
    case 'checkbox':
    case 'toggle':
      return true;
    case 'email':
      return 'user@example.com';
    case 'phone':
    case 'tel':
      return '+1-555-0100';
    case 'url':
      return 'https://example.com';
    case 'text':
    case 'string':
      return 'Sample text';
    case 'textarea':
    case 'longtext':
      return 'Sample longer text content';
    case 'select':
    case 'radio':
    case 'dropdown':
      return 'option1';
    case 'multiselect':
    case 'checkboxgroup':
      return ['option1', 'option2'];
    case 'file':
    case 'image':
    case 'media':
    case 'attachment':
      return 'https://example.com/files/sample.pdf';
    case 'location':
    case 'gps':
      return { lat: 37.7749, lng: -122.4194 };
    default:
      return 'Sample value';
  }
};

export const buildSamplePayload = (fields) => {
  const payload = {};
  fields.forEach(f => {
    payload[f.key] = getSampleValue(f);
  });
  return { fields: payload };
};

export const formatJson = (obj) => JSON.stringify(obj, null, 2);

const formatValue = (val) => {
  if (typeof val === 'string') return `"${val}"`;
  if (Array.isArray(val)) return JSON.stringify(val);
  if (typeof val === 'object' && val !== null) return JSON.stringify(val);
  return String(val);
};

const fieldLabel = (f) => {
  const req = f.required ? 'required' : 'optional';
  return `${f.title || f.key} (${f.type}, ${req})`;
};

const buildFieldRefBlock = (fields, prefix) => {
  if (!fields.length) return '';
  const lines = fields.map(f =>
    `${prefix}   ${f.key} — ${fieldLabel(f)}`
  );
  return `${prefix} Field Schema:\n${lines.join('\n')}`;
};

const buildAnnotatedFields = (fields, { comment, keyFmt, sep, indent, trailing = false }) => {
  return fields.map((f, i) => {
    const valStr = formatValue(getSampleValue(f));
    const comma = (i < fields.length - 1 || trailing) ? ',' : '';
    const cmt = `${indent}${comment} ${fieldLabel(f)}`;
    const key = keyFmt(f.key);
    return `${cmt}\n${indent}${key}${sep}${valStr}${comma}`;
  }).join('\n');
};

export const generateCurlExample = ({ webhookUrl, fields = [] }) => {
  const samplePayload = buildSamplePayload(fields);
  const body = formatJson(samplePayload);
  const ref = buildFieldRefBlock(fields, '#');
  return `${ref ? ref + '\n\n' : ''}curl -X POST \\
  "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "X-Idempotency-Key: $(uuidgen)" \\
  -d '${body}'`;
};

export const generatePythonExample = ({ webhookUrl, fields = [] }) => {
  const annotated = buildAnnotatedFields(fields, {
    comment: '#',
    keyFmt: k => `"${k}"`,
    sep: ': ',
    indent: '        ',
    trailing: true,
  });

  return `import requests
import uuid

url = "${webhookUrl}"

headers = {
    "Content-Type": "application/json",
    "X-Idempotency-Key": str(uuid.uuid4())
}

payload = {
    "fields": {
${annotated}
    }
}

response = requests.post(url, json=payload, headers=headers)

if response.status_code == 200:
    result = response.json()
    print(f"Submission created: {result['submissionId']}")
elif response.status_code == 422:
    errors = response.json()
    print(f"Validation errors: {errors['details']}")
else:
    print(f"Error: {response.status_code} - {response.text}")`;
};

export const generateJavaScriptExample = ({ webhookUrl, fields = [] }) => {
  const annotated = buildAnnotatedFields(fields, {
    comment: '//',
    keyFmt: k => `"${k}"`,
    sep: ': ',
    indent: '    ',
    trailing: true,
  });

  return `const webhookUrl = "${webhookUrl}";

const payload = {
  fields: {
${annotated}
  }
};

const response = await fetch(webhookUrl, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Idempotency-Key": crypto.randomUUID()
  },
  body: JSON.stringify(payload)
});

const result = await response.json();

if (response.ok) {
  console.log("Submission created:", result.submissionId);
} else if (response.status === 422) {
  console.error("Validation errors:", result.details);
} else {
  console.error("Error:", response.status, result);
}`;
};

export const generateCSharpExample = ({ webhookUrl, fields = [] }) => {
  const annotated = buildAnnotatedFields(fields, {
    comment: '//',
    keyFmt: k => `["${k}"]`,
    sep: ' = ',
    indent: '    ',
  });

  return `using System.Net.Http;
using System.Text;
using System.Text.Json;

var client = new HttpClient();
var url = "${webhookUrl}";

// Build the payload from field schema
var fields = new Dictionary<string, object>
{
${annotated}
};

var payload = new { fields };
var json = JsonSerializer.Serialize(payload);
var content = new StringContent(json, Encoding.UTF8, "application/json");
content.Headers.Add("X-Idempotency-Key", Guid.NewGuid().ToString());

var response = await client.PostAsync(url, content);
var responseBody = await response.Content.ReadAsStringAsync();

if (response.IsSuccessStatusCode)
{
    var result = JsonSerializer.Deserialize<JsonElement>(responseBody);
    Console.WriteLine($"Submission created: {result.GetProperty("submissionId")}");
}
else
{
    Console.WriteLine($"Error: {response.StatusCode} - {responseBody}");
}`;
};

export const generatePhpExample = ({ webhookUrl, fields = [] }) => {
  const annotated = buildAnnotatedFields(fields, {
    comment: '//',
    keyFmt: k => `"${k}"`,
    sep: ' => ',
    indent: '        ',
    trailing: true,
  });

  return `<?php

$url = "${webhookUrl}";

$payload = [
    "fields" => [
${annotated}
    ]
];

$json = json_encode($payload);

$ch = curl_init($url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $json);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Content-Type: application/json",
    "X-Idempotency-Key: " . uniqid("", true)
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);

if ($httpCode === 200) {
    echo "Submission created: " . $result["submissionId"];
} elseif ($httpCode === 422) {
    echo "Validation errors: " . print_r($result["details"], true);
} else {
    echo "Error: $httpCode - $response";
}`;
};

export const generatePostmanCollection = ({ webhookUrl, fields = [] }) => {
  const samplePayload = buildSamplePayload(fields);
  const fieldDocs = fields.map(f => {
    const req = f.required ? 'Required' : 'Optional';
    return `- **${f.key}** (\`${f.type}\`, ${req}): ${f.title || f.key}`;
  }).join('\n');

  const collection = {
    info: {
      name: 'Inbound Webhook',
      description: `Submit form data to the inbound webhook endpoint.\n\n## Fields\n\n${fieldDocs || 'No fields defined.'}`,
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    variable: fields.map(f => ({
      key: f.key,
      value: String(getSampleValue(f)),
      type: 'string',
      description: `${f.title || f.key} (${f.type}, ${f.required ? 'required' : 'optional'})`,
    })),
    item: [
      {
        name: 'Submit Form Data',
        request: {
          method: 'POST',
          description: `Posts form submission data.\n\n### Field Schema\n\n${fieldDocs || 'No fields defined.'}`,
          header: [
            { key: 'Content-Type', value: 'application/json' },
            { key: 'X-Idempotency-Key', value: '{{$guid}}' },
          ],
          body: {
            mode: 'raw',
            raw: formatJson(samplePayload),
          },
          url: {
            raw: webhookUrl,
            protocol: webhookUrl.startsWith('https') ? 'https' : 'http',
            host: [webhookUrl.split('//')[1]?.split('/')[0] || 'localhost'],
            path: webhookUrl.split('//')[1]?.split('/').slice(1) || [],
          },
        },
      },
    ],
  };
  return JSON.stringify(collection, null, 2);
};

const getHtmlInputType = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'number': case 'numeric': return 'number';
    case 'decimal': case 'currency': return 'number';
    case 'email': return 'email';
    case 'date': return 'date';
    case 'datetime': case 'date-time': return 'datetime-local';
    case 'time': return 'time';
    case 'url': return 'url';
    case 'phone': case 'tel': return 'tel';
    case 'boolean': case 'checkbox': case 'toggle': return 'checkbox';
    default: return 'text';
  }
};

const isTextarea = (type) => ['textarea', 'longtext'].includes((type || '').toLowerCase());
const isSelect = (type) => ['select', 'radio', 'dropdown', 'multiselect', 'checkboxgroup'].includes((type || '').toLowerCase());
const isCheckbox = (type) => ['boolean', 'checkbox', 'toggle'].includes((type || '').toLowerCase());

export const generateHtmlFormExample = ({ webhookUrl, fields = [] }) => {
  const formFields = fields.map(f => {
    const req = f.required ? ' required' : '';
    const reqMark = f.required ? '<span class="required">*</span>' : '';
    const label = f.title || f.key;

    if (isCheckbox(f.type)) {
      return `      <div class="form-group checkbox-group">
        <input type="checkbox" id="${f.key}" name="${f.key}" />
        <label for="${f.key}" class="checkbox-label">${label} ${reqMark}</label>
      </div>`;
    }

    if (isSelect(f.type) && f.options?.length) {
      const multi = ['multiselect', 'checkboxgroup'].includes((f.type || '').toLowerCase()) ? ' multiple' : '';
      const opts = f.options.map(o => {
        const val = o.value || o;
        const text = o.label || o.value || o;
        return `          <option value="${val}">${text}</option>`;
      }).join('\n');
      return `      <div class="form-group">
        <label for="${f.key}">${label} ${reqMark}</label>
        <select id="${f.key}" name="${f.key}"${multi}${req}>
          <option value="">Select...</option>
${opts}
        </select>
      </div>`;
    }

    if (isTextarea(f.type)) {
      return `      <div class="form-group">
        <label for="${f.key}">${label} ${reqMark}</label>
        <textarea id="${f.key}" name="${f.key}" placeholder="Enter ${label.toLowerCase()}"${req}></textarea>
      </div>`;
    }

    const inputType = getHtmlInputType(f.type);
    const step = ['decimal', 'currency'].includes((f.type || '').toLowerCase()) ? ' step="0.01"' : '';
    return `      <div class="form-group">
        <label for="${f.key}">${label} ${reqMark}</label>
        <input type="${inputType}" id="${f.key}" name="${f.key}" placeholder="Enter ${label.toLowerCase()}"${step}${req} />
      </div>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Webhook Form Submission</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background: #f5f5f7; padding: 2rem 1rem; color: #1a1a2e;
    }
    .container {
      max-width: 560px; margin: 0 auto; background: #fff;
      border-radius: 12px; padding: 2rem;
      box-shadow: 0 2px 12px rgba(35, 37, 98, 0.08);
    }
    h1 { font-size: 1.4rem; margin-bottom: 0.25rem; }
    .subtitle { color: #666; font-size: 0.85rem; margin-bottom: 1.5rem; }
    .form-group { margin-bottom: 1.25rem; }
    label {
      display: block; font-weight: 600; font-size: 0.85rem;
      margin-bottom: 0.35rem; color: #333;
    }
    .required { color: #e53e3e; margin-left: 2px; }
    input, select, textarea {
      width: 100%; padding: 0.55rem 0.75rem;
      border: 1px solid #d1d5db; border-radius: 8px;
      font-size: 0.9rem; color: #1a1a2e; background: #fff;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    input:focus, select:focus, textarea:focus {
      outline: none; border-color: #59caee;
      box-shadow: 0 0 0 3px rgba(89, 202, 238, 0.2);
    }
    textarea { min-height: 80px; resize: vertical; }
    .checkbox-group {
      display: flex; align-items: center; gap: 0.5rem;
      padding-top: 0.25rem;
    }
    .checkbox-group input { width: auto; }
    .checkbox-label { margin-bottom: 0; font-weight: 500; }
    button[type="submit"] {
      width: 100%; padding: 0.7rem; margin-top: 0.5rem;
      background: #232562; color: #fff; border: none;
      border-radius: 8px; font-size: 0.95rem;
      font-weight: 600; cursor: pointer;
      transition: background 0.15s;
    }
    button[type="submit"]:hover { background: #1a1c4e; }
    button[type="submit"]:disabled { opacity: 0.6; cursor: not-allowed; }
    #result {
      margin-top: 1rem; padding: 0.85rem 1rem;
      border-radius: 8px; font-size: 0.85rem; display: none;
    }
    #result.success {
      display: block; background: #f0fff4;
      border: 1px solid #c6f6d5; color: #276749;
    }
    #result.error {
      display: block; background: #fff5f5;
      border: 1px solid #fed7d7; color: #9b2c2c;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>Webhook Form</h1>
    <p class="subtitle">Submit data to the inbound webhook endpoint</p>
    <form id="webhookForm">
${formFields}
      <button type="submit">Submit</button>
    </form>
    <div id="result"></div>
  </div>
  <script>
    const WEBHOOK_URL = "${webhookUrl}";

    document.getElementById("webhookForm").addEventListener("submit", async (e) => {
      e.preventDefault();
      const btn = e.target.querySelector("button[type=submit]");
      const resultDiv = document.getElementById("result");
      btn.disabled = true;
      btn.textContent = "Submitting...";
      resultDiv.className = "";
      resultDiv.style.display = "none";

      const formData = new FormData(e.target);
      const fields = {};
      for (const [key, value] of formData.entries()) {
        const input = e.target.querySelector('[name="' + key + '"]');
        if (input && input.type === "number") {
          fields[key] = parseFloat(value) || 0;
        } else {
          fields[key] = value;
        }
      }
      // Handle unchecked checkboxes (they don't appear in FormData)
      e.target.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        fields[cb.name] = cb.checked;
      });

      try {
        const response = await fetch(WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Idempotency-Key": crypto.randomUUID()
          },
          body: JSON.stringify({ fields })
        });
        const data = await response.json();
        if (response.ok) {
          resultDiv.className = "success";
          resultDiv.textContent = "Submitted successfully! ID: " + (data.submissionId || "—");
        } else {
          resultDiv.className = "error";
          resultDiv.textContent = "Error: " + (data.message || JSON.stringify(data));
        }
      } catch (err) {
        resultDiv.className = "error";
        resultDiv.textContent = "Network error: " + err.message;
      }
      resultDiv.style.display = "block";
      btn.disabled = false;
      btn.textContent = "Submit";
    });
  </script>
</body>
</html>`;
};

export const generateCodeExamples = (config) => {
  const { webhookUrl, token, fields = [] } = config;
  const fullConfig = { webhookUrl, token, fields };

  return [
    { label: 'cURL', language: 'bash', code: generateCurlExample(fullConfig) },
    { label: 'Python', language: 'python', code: generatePythonExample(fullConfig) },
    { label: 'JavaScript', language: 'javascript', code: generateJavaScriptExample(fullConfig) },
    { label: 'C#', language: 'csharp', code: generateCSharpExample(fullConfig) },
    { label: 'PHP', language: 'php', code: generatePhpExample(fullConfig) },
    { label: 'Postman', language: 'json', code: generatePostmanCollection(fullConfig) },
    { label: 'HTML', language: 'html', code: generateHtmlFormExample(fullConfig) },
  ];
};
